import { Button } from '@forma/ui';
import type { AppLanguage, Translator } from '../i18n';
import { cn } from '../lib/utils';
import { getPublicSiteLegalLinks } from '../features/app/public-site-links';

/**
 * Telemetry consent banner. Rendered only when `useConsent().shouldPrompt` is
 * true (EU/UK region, no decision yet). Accept and Reject carry equal visual
 * weight, so refusing is not made harder than accepting — required under
 * GDPR/ePrivacy. No telemetry has run at this point; nothing loads until the
 * user chooses "Accept".
 */
export function ConsentBanner({
  appLanguage,
  onAccept,
  onReject,
  compact = false,
  t,
}: {
  appLanguage: AppLanguage;
  onAccept: () => void;
  onReject: () => void;
  compact?: boolean;
  t: Translator;
}) {
  const legalLinks = getPublicSiteLegalLinks(appLanguage);

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4',
        compact && 'pointer-events-none sm:flex sm:justify-end sm:px-6',
      )}
      role="region"
      aria-label={t('Privacy choices')}
    >
      <section
        aria-live="polite"
        className={cn(
          'mx-auto flex max-w-2xl flex-col gap-4 rounded-[24px] border border-stone-200 bg-white/95 p-4 text-stone-800 shadow-xl backdrop-blur sm:flex-row sm:items-center sm:justify-between',
          compact &&
            'pointer-events-auto max-w-md gap-3 rounded-[20px] p-3 sm:mx-0 sm:flex-col sm:items-stretch',
        )}
        role="status"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-stone-900">
            {t('Help us improve Forma')}
          </p>
          <p className="mt-1 text-sm leading-6 text-stone-600">
            {t(
              'We use privacy-friendly analytics and crash reporting to keep Forma fast and stable. Nothing loads until you choose.',
            )}{' '}
            <a
              className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
              href={legalLinks.privacy}
            >
              {t('Privacy Policy')}
            </a>
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            className="min-h-11 flex-1 justify-center sm:flex-none"
            onClick={onReject}
            size="sm"
            type="button"
            variant="outline"
          >
            {t('Reject')}
          </Button>
          <Button
            className="min-h-11 flex-1 justify-center sm:flex-none"
            onClick={onAccept}
            size="sm"
            variant="accent"
            type="button"
          >
            {t('Accept')}
          </Button>
        </div>
      </section>
    </div>
  );
}
